import { useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { TutorialVideo } from '@/types';
import { groupTutorials } from '@/lib/helpCenter';
import { TutorialPlayerModal } from '@/components/TutorialPlayerModal';

interface HelpViewProps {
  tutorials: TutorialVideo[];
  /** Aufgerufen wenn ein Video im Player angezeigt wird (markiert gelesen + trackt). */
  onVideoOpened: (video: TutorialVideo) => void;
}

export const HelpView: React.FC<HelpViewProps> = ({ tutorials, onVideoOpened }) => {
  const [playerVideos, setPlayerVideos] = useState<TutorialVideo[] | null>(null);

  const groups = groupTutorials(tutorials);

  if (tutorials.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <PlayCircle className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-sm font-medium text-foreground">Noch keine Tutorials verfügbar</p>
        <p className="text-xs text-muted-foreground mt-1">Schau bald wieder vorbei.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-10">
      {groups.map(group => (
        <section key={group.category}>
          <h2 className="text-lg font-semibold text-foreground mb-4">{group.category}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {group.videos.map(video => (
              <button
                key={video.id}
                type="button"
                onClick={() => setPlayerVideos([video])}
                className="group text-left rounded-xl border border-border bg-card overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="relative aspect-video bg-muted">
                  <img
                    src={`https://i.ytimg.com/vi/${video.youtubeVideoId}/hqdefault.jpg`}
                    alt={video.title}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                    <PlayCircle className="h-12 w-12 text-white drop-shadow" />
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm font-semibold text-foreground leading-snug">{video.title}</p>
                  {video.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{video.description}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}

      <TutorialPlayerModal
        videos={playerVideos ?? []}
        open={playerVideos !== null}
        onOpenChange={(open) => { if (!open) setPlayerVideos(null); }}
        onViewed={onVideoOpened}
      />
    </div>
  );
};
